import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Trophy, Crown, Loader2 } from "lucide-react";
import { leaderboardFor, weekWinner } from "../lib/leaderboard";
import { getLeaderboardDataset } from "../lib/api";

const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

const Cell = ({ children, strong, testid }) => (
  <td data-testid={testid} className={`px-2 sm:px-3 py-2.5 text-right font-mono ${strong ? "text-white font-bold" : "text-slate-300"}`}>{children}</td>
);

export const Leaderboard = () => {
  const [leads, setLeads] = useState(null);
  const [missingNames, setMissingNames] = useState(false);
  const [offset, setOffset] = useState(0);
  const [mode, setMode] = useState("week");

  useEffect(() => {
    getLeaderboardDataset().then(({ data }) => {
      setLeads(data);
      setMissingNames(data.some((l) => l.posted_by && !l.posted_by_name));
    }).catch(() => setLeads([]));
  }, []);

  if (leads === null) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;

  const { start, end, list, total } = leaderboardFor(leads, offset, mode);
  const winner = weekWinner(leads, offset, mode);
  const isMonth = mode === "month";
  const switchMode = (m) => { setMode(m); setOffset(0); };
  const periodLabel = isMonth
    ? (offset === 0 ? "This month" : start.toLocaleDateString(undefined, { month: "long", year: "numeric" }))
    : (offset === 0 ? "This week" : `${fmt(start)} – ${fmt(end)}`);

  return (
    <div data-testid="leaderboard-page" className="bg-[#13161C] border border-[#262C38] rounded-md min-w-0 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#262C38]">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-400" />
          <span className="font-heading font-bold uppercase tracking-wide text-white">{isMonth ? "Monthly" : "Weekly"} leaderboard</span>
          <div className="flex ml-2 border border-[#262C38] rounded overflow-hidden">
            {["week", "month"].map((m) => (
              <button key={m} data-testid={`leaderboard-mode-${m}-btn`} onClick={() => switchMode(m)}
                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest transition-colors ${mode === m ? "bg-[#F95722] text-white" : "text-slate-400 hover:text-white"}`}>
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="leaderboard-prev-week-btn" onClick={() => setOffset((o) => o - 1)} className="p-1.5 rounded border border-[#262C38] text-slate-400 hover:text-white"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="leaderboard-week-label" className="font-mono text-xs text-slate-300 px-2 min-w-[104px] text-center">
            {periodLabel}
          </span>
          <button data-testid="leaderboard-next-week-btn" onClick={() => setOffset((o) => Math.min(0, o + 1))} disabled={offset === 0} className="p-1.5 rounded border border-[#262C38] text-slate-400 hover:text-white disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-2 sm:px-3 py-2 text-left">Setter</th>
              <th className="px-2 sm:px-3 py-2 text-right">Leads</th>
              <th className="px-2 sm:px-3 py-2 text-right">Applied</th>
              <th className="px-2 sm:px-3 py-2 text-right">Approved</th>
              <th className="px-2 sm:px-3 py-2 text-right">Hired</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={r.name} data-testid={`leaderboard-row-${i}`} className="border-t border-[#262C38]">
                <td className="px-2 sm:px-3 py-2.5 text-white">
                  <span className="font-mono text-xs text-slate-500 mr-2">{i + 1}</span>{r.name}
                  {winner === r.name && !isMonth && <Trophy data-testid="leaderboard-winner-trophy" className="inline h-3.5 w-3.5 ml-1.5 text-amber-400" />}
                  {winner === r.name && isMonth && (
                    <span data-testid="leaderboard-champion-trophy" className="inline-flex items-center gap-1 ml-2 text-[9px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-gradient-to-r from-yellow-300 to-amber-500 text-black">
                      <Crown className="h-2.5 w-2.5" /> {offset === 0 ? "Leading" : "Champion"}
                    </span>
                  )}
                </td>
                <Cell>{r.posted}</Cell>
                <Cell>{r.applied}</Cell>
                <Cell>{r.approved}</Cell>
                <Cell strong>{r.hired}</Cell>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500 text-xs font-mono">No activity this {mode} yet.</td></tr>
            )}
            <tr className="border-t-2 border-[#384152] bg-[#0B0C0E]">
              <td className="px-2 sm:px-3 py-2.5 font-bold text-slate-200 uppercase text-xs tracking-wide">Team total</td>
              <Cell testid="leaderboard-total-posted">{total.posted}</Cell>
              <Cell testid="leaderboard-total-applied">{total.applied}</Cell>
              <Cell testid="leaderboard-total-approved">{total.approved}</Cell>
              <Cell strong testid="leaderboard-total-hired">{total.hired}</Cell>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="px-4 py-2 text-[11px] font-mono text-slate-500 border-t border-[#262C38]">{isMonth ? "Monthly Champion" : "Weekly winner"} = most Hired (ties: Approved, Applied, then leads posted). {isMonth ? "Calendar months." : "Mon–Sun weeks."}</p>
      {missingNames && (
        <p data-testid="leaderboard-names-hint" className="px-4 py-3 text-[11px] font-mono text-amber-300/80 border-t border-[#262C38]">
          Some setter names show as IDs until the Slack app gets the users:read permission (then reinstall and press Sync).
        </p>
      )}
    </div>
  );
};
